/**
 * frontend/src/pages/DashboardPage.jsx
 *
 * Planned work against what actually happened.
 *
 * Every figure on this page is read from the database through
 * /api/dashboard. Nothing is illustrative and nothing is padded: where there
 * is no data the card says so rather than showing a confident zero, because a
 * dashboard that cannot tell "none" from "none yet" is worse than no
 * dashboard.
 *
 * Colours follow the Q-Lana palette. Red appears only where something is
 * genuinely wrong — late work and overdue items — never as decoration.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Flag, FolderKanban, CheckSquare, AlertCircle, TrendingUp, TrendingDown, Minus,
} from 'lucide-react';
import { dashboardAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { STATUS_STYLE } from '../utils/workStatus';

/* ── Palette ──────────────────────────────────────────────────────────── */
const SAPPHIRE = '#2E539F';
const JAVA     = '#5CC3BC';
const SUNGLOW  = '#FFC849';
const STEEL    = '#7F8C8D';
const CRIMSON  = '#C8372D';

/* Project statuses are their own vocabulary, not the work-status one. */
const PROJECT_COLOR = {
  'Active':      SAPPHIRE,
  'Not Started': STEEL,
  'On Hold':     SUNGLOW,
  'Completed':   JAVA,
};
const statusColor = (s) => PROJECT_COLOR[s] || STATUS_STYLE[s]?.dot || STEEL;

const PERIODS = [
  { key: '48h', label: '48h' }, { key: '1W', label: '1W' }, { key: '1M', label: '1M' },
  { key: '3M',  label: '3M'  }, { key: '1Y', label: '1Y' }, { key: 'all', label: 'All' },
];

/* ── Small pieces ─────────────────────────────────────────────────────── */

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

/** A figure with no value yet. Deliberately not "0". */
const Dash = () => <span style={{ color:'var(--text-secondary)' }}>&ndash;</span>;

const Card = ({ title, subtitle, right, children, className = '' }) => (
  <div className={`card ${className}`}>
    {(title || right) && (
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          {title && <h2 className="font-semibold" style={{ color:'var(--text)' }}>{title}</h2>}
          {subtitle && <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{subtitle}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </div>
);

const Kpi = ({ icon: Icon, value, label, sub, accent = SAPPHIRE, badge }) => (
  <div className="card py-4">
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-2.5">
        <Icon size={20} style={{ color: accent }} />
        <p className="text-3xl font-bold leading-none" style={{ color: accent }}>{value}</p>
      </div>
      {badge}
    </div>
    <p className="text-sm font-medium mt-2" style={{ color:'var(--text)' }}>{label}</p>
    <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>{sub}</p>
  </div>
);

const Tile = ({ label, value, sub, color }) => (
  <div className="rounded-xl p-4" style={{ background:'var(--surface-alt)' }}>
    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{label}</p>
    <p className="text-3xl font-bold mt-1.5 leading-none" style={{ color: color || 'var(--text)' }}>{value}</p>
    <p className="text-xs mt-1.5" style={{ color:'var(--text-secondary)' }}>{sub}</p>
  </div>
);

/** Recharts tooltip. Kept outside the page component so it never closes over state. */
const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-lg"
      style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
      {label && <p className="font-semibold mb-1" style={{ color:'var(--text)' }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>
          {p.name}: <strong>{typeof p.value === 'number' ? Math.round(p.value * 100) / 100 : p.value}</strong>
        </p>
      ))}
    </div>
  );
};

const NoData = ({ msg = 'Nothing recorded for this period' }) => (
  <div className="flex items-center justify-center h-56 text-sm" style={{ color:'var(--text-secondary)' }}>
    {msg}
  </div>
);

const axis = { stroke:'var(--text-secondary)', fontSize:11, tickLine:false, axisLine:false };

/* ── Page ─────────────────────────────────────────────────────────────── */

export default function DashboardPage() {
  const { user } = useAuth();

  const [global,  setGlobal]  = useState(null);
  const [profile, setProfile] = useState(null);
  const [hours,   setHours]   = useState([]);
  const [period,  setPeriod]  = useState('1W');
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const firstLoad = useRef(true);

  /**
   * Background refresh. console.error rather than a toast: this fires every
   * sixty seconds whether or not anyone is looking, and a popup every minute
   * because the laptop slept is not a useful thing to build.
   */
  const load = useCallback(async (p) => {
    try {
      const [g, e, h] = await Promise.all([
        dashboardAPI.getGlobal(),
        dashboardAPI.getExecutionProfile(p),
        dashboardAPI.getEffortTrend(p),
      ]);
      setGlobal(g.data);
      setProfile(e.data);
      setHours(h.data || []);
      setError('');
    } catch (err) {
      console.error('dashboard load failed:', err);
      setError('Could not refresh the dashboard. The figures below may be out of date.');
    } finally {
      setLoading(false);
      firstLoad.current = false;
    }
  }, []);

  useEffect(() => { load(period); }, [load, period]);

  // Live refresh, matching the badge in the header.
  useEffect(() => {
    const t = setInterval(() => load(period), 60000);
    return () => clearInterval(t);
  }, [load, period]);

  if (loading && firstLoad.current) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="w-8 h-8 border-2 rounded-full animate-spin"
          style={{ borderColor:`${SAPPHIRE}33`, borderTopColor:SAPPHIRE }} />
      </div>
    );
  }

  /* All derived values are declared AFTER the hooks above and read only
     below. A const read above its own declaration crashes the page at
     render, which is how Projects and Activities went down. */
  const stats   = global?.stats || {};
  const ms      = global?.milestones || { total:0, completed:0, in_progress:0 };
  const due     = global?.due_today || { total:0, done:0 };
  const dueePct = due.total ? Math.round(due.done / due.total * 100) : 0;

  const projectsPie = (global?.projects_by_status || [])
    .map(r => ({ name:r.status, value:r.count }));
  const tasksPie = (global?.tasks_by_status || [])
    .map(r => ({ name:r.status, value:r.count }));
  const activityBars = (global?.activities_by_status || [])
    .map(r => ({ name:r.status, value:r.count }));
  const projectTotal  = projectsPie.reduce((s, r) => s + r.value, 0);
  const taskTotal     = tasksPie.reduce((s, r) => s + r.value, 0);
  const activityTotal = activityBars.reduce((s, r) => s + r.value, 0);

  const onTime   = profile?.on_time_rate;            // null when nothing has a due date
  const slip     = profile?.avg_slippage_days;       // negative means early
  const hardest  = profile?.hardest_week;
  const overdue  = profile?.currently_overdue ?? 0;
  const weekly   = (profile?.weekly_completion || []).map(w => ({ label:w.label, pct:w.pct }));
  const onTimeSeries = profile?.on_time_series || [];
  const overdueList  = profile?.overdue_activities || [];
  const progress     = global?.project_progress || [];

  const slipLabel = slip === null || slip === undefined
    ? <Dash />
    : slip < 0 ? `${Math.abs(Math.round(slip * 10) / 10)}d early`
    : slip > 0 ? `${Math.round(slip * 10) / 10}d late`
    : 'on the day';
  const slipColor = slip === null || slip === undefined ? undefined
    : slip > 0 ? CRIMSON : JAVA;
  const TrendIcon = onTime === null ? Minus : onTime >= 80 ? TrendingUp : TrendingDown;
  const trendColor = onTime === null ? STEEL : onTime >= 80 ? JAVA : CRIMSON;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold" style={{ color:'var(--text)' }}>
            {greeting()}, {user?.name?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>
            {new Date().toLocaleDateString(undefined,
              { weekday:'long', year:'numeric', month:'long', day:'numeric' })}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: trendColor }}>
            <TrendIcon size={16} />
            {onTime === null ? 'No due dates yet' : `${onTime}% on time`}
          </span>
          <span className="text-xs px-2.5 py-1 rounded-full"
            style={{ background:`${JAVA}22`, color:'#1f7a74' }}>
            Live &middot; 60s refresh
          </span>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-xl text-sm"
          style={{ background:`${CRIMSON}14`, color:CRIMSON, border:`1px solid ${CRIMSON}33` }}>
          {error}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Flag} accent={SAPPHIRE}
          value={`${ms.completed}/${ms.total}`} label="Milestones"
          sub={`${ms.in_progress} in progress`} />
        <Kpi icon={FolderKanban} accent={SAPPHIRE}
          value={stats.active_projects ?? 0} label="Active Projects"
          sub={`${stats.total_projects ?? 0} total`} />
        <Kpi icon={CheckSquare} accent={due.total ? SAPPHIRE : STEEL}
          value={due.total} label="Due Today"
          sub={`${due.done} done · ${dueePct}%`} />
        <Kpi icon={AlertCircle} accent={overdue ? CRIMSON : JAVA}
          value={overdue} label="Overdue" sub="activities past due" />
      </div>

      {/* Execution profile */}
      <Card title="Your Execution Profile"
        subtitle="Planned due dates vs actual completion, all time">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile label="On-time rate"
            value={onTime === null ? <Dash /> : `${onTime}%`}
            color={onTime === null ? undefined : onTime >= 80 ? JAVA : CRIMSON}
            sub={profile ? `${profile.on_time_count} of ${profile.completed_with_due} with due dates` : ''} />
          <Tile label="Average slippage" value={slipLabel} color={slipColor}
            sub={slip === null || slip === undefined ? 'Nothing completed with a due date yet'
                 : slip <= 0 ? 'Consistently ahead or on time' : 'Finishing after the due date'} />
          <Tile label="Hardest week (12 wks)"
            value={hardest ? `${hardest.pct}%` : <Dash />}
            color={hardest ? (hardest.pct >= 80 ? JAVA : CRIMSON) : undefined}
            sub={hardest ? `${hardest.label} · ${hardest.late} late` : 'No completions in the last 12 weeks'} />
          <Tile label="Currently overdue" value={overdue}
            color={overdue ? CRIMSON : JAVA} sub="past their due date" />
        </div>
      </Card>

      {/* Period */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wider mr-1"
          style={{ color:'var(--text-secondary)' }}>Period</span>
        {PERIODS.map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)}
            className="text-sm px-3.5 py-1.5 rounded-full transition-colors"
            style={period === p.key
              ? { background:SAPPHIRE, color:'#fff', fontWeight:600 }
              : { background:'var(--surface)', color:'var(--text-secondary)', border:'1px solid var(--border)' }}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Hours and completion rate */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Hours: Planned vs Executed"
          subtitle="Estimated hours against time actually logged">
          {hours.length === 0 ? <NoData /> : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={hours} margin={{ left:-18, right:8, top:4 }}>
                <defs>
                  <linearGradient id="plannedFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={SAPPHIRE} stopOpacity={0.22} />
                    <stop offset="100%" stopColor={SAPPHIRE} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" {...axis} />
                <YAxis {...axis} />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize:12 }} />
                <Area type="monotone" dataKey="estimated_hours" name="Planned hrs"
                  stroke={SAPPHIRE} strokeWidth={2} fill="url(#plannedFill)" />
                <Area type="monotone" dataKey="actual_hours" name="Executed hrs"
                  stroke={JAVA} strokeWidth={2} fill="none" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Weekly Completion Rate"
          subtitle="Share of activities completed on or before their due date">
          {weekly.length === 0 ? <NoData msg="No completed activities with due dates yet" /> : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={weekly} margin={{ left:-18, right:8, top:4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" {...axis} />
                <YAxis domain={[0,100]} unit="%" {...axis} />
                <Tooltip content={<ChartTip />} />
                <Line type="monotone" dataKey="pct" name="On time"
                  stroke={SAPPHIRE} strokeWidth={2.5} dot={{ r:3, fill:SAPPHIRE }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Status breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Projects by Status" subtitle={`${projectTotal} total`}>
          {projectTotal === 0 ? <NoData msg="No projects yet" /> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={projectsPie} dataKey="value" nameKey="name"
                  cx="50%" cy="50%" outerRadius={82} label={({ percent }) => `${Math.round(percent*100)}%`}>
                  {projectsPie.map((d, i) => <Cell key={i} fill={statusColor(d.name)} />)}
                </Pie>
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize:12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Tasks by Status" subtitle={`${taskTotal} total`}>
          {taskTotal === 0 ? <NoData msg="No tasks yet" /> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={tasksPie} dataKey="value" nameKey="name"
                  cx="50%" cy="50%" innerRadius={48} outerRadius={82}
                  label={({ percent }) => `${Math.round(percent*100)}%`}>
                  {tasksPie.map((d, i) => <Cell key={i} fill={statusColor(d.name)} />)}
                </Pie>
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize:12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card title="Activities by Status" subtitle={`${activityTotal} total`}>
          {activityTotal === 0 ? <NoData msg="No activities yet" /> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={activityBars} margin={{ left:-22, right:8, top:8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="name" {...axis} interval={0} angle={-18} textAnchor="end" height={54} />
                <YAxis allowDecimals={false} {...axis} />
                <Tooltip content={<ChartTip />} />
                <Bar dataKey="value" name="Activities" radius={[4,4,0,0]}>
                  {activityBars.map((d, i) => <Cell key={i} fill={statusColor(d.name)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* On-time performance */}
      <Card title="On-Time Performance" subtitle="Planned due dates vs actual completion"
        right={
          <div className="text-right">
            <p className="text-2xl font-bold" style={{ color: trendColor }}>
              {onTime === null ? <Dash /> : `${onTime}%`}
            </p>
            <p className="text-xs" style={{ color:'var(--text-secondary)' }}>on time</p>
          </div>
        }>
        {onTimeSeries.length === 0 ? <NoData /> : (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={onTimeSeries} margin={{ left:-22, right:8, top:8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="label" {...axis} />
                <YAxis allowDecimals={false} {...axis} />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize:12 }} />
                <Bar dataKey="on_time" name="On time" stackId="a" fill={JAVA}     radius={[0,0,0,0]} />
                <Bar dataKey="late"    name="Late"    stackId="a" fill={CRIMSON}  radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex justify-between items-center text-xs pt-3 mt-1 border-t"
              style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
              <span>Average slippage: <strong style={{ color: slipColor }}>{slipLabel}</strong></span>
              <span>{overdue} currently overdue</span>
            </div>
          </>
        )}
      </Card>

      {/* Project progress and overdue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Project Progress" subtitle="Top projects by task completion">
          {progress.length === 0 ? (
            <NoData msg="No projects yet" />
          ) : (
            <div className="divide-y" style={{ borderColor:'var(--border)' }}>
              {progress.map(p => (
                <Link key={p.id} to={`/projects/${p.id}`}
                  className="flex items-center gap-3 py-2.5 hover:opacity-80 transition-opacity">
                  <span className="flex-1 min-w-0 truncate text-sm" style={{ color:'var(--text)' }}>
                    {p.name}
                  </span>
                  <span className="badge text-xs whitespace-nowrap"
                    style={{ background:`${statusColor(p.status)}1a`, color:statusColor(p.status) }}>
                    {p.status}
                  </span>
                  <span className="w-24 h-1.5 rounded-full overflow-hidden flex-shrink-0"
                    style={{ background:'var(--surface-alt)' }}>
                    <span className="block h-full rounded-full"
                      style={{ width:`${p.progress}%`, background: p.progress === 100 ? JAVA : SAPPHIRE }} />
                  </span>
                  <span className="text-sm font-semibold w-11 text-right flex-shrink-0"
                    style={{ color:'var(--text-secondary)' }}>
                    {p.task_count === 0 ? <Dash /> : `${p.progress}%`}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card title="Overdue Activities"
          right={
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
              style={{ background: overdue ? `${CRIMSON}1a` : `${JAVA}1a`,
                       color: overdue ? CRIMSON : '#1f7a74' }}>
              {overdue}
            </span>
          }>
          {overdueList.length === 0 ? (
            <NoData msg="Nothing is past its due date" />
          ) : (
            <div className="divide-y" style={{ borderColor:'var(--border)' }}>
              {overdueList.map(a => (
                <Link key={a.id} to={`/activities/${a.id}`}
                  className="flex items-start justify-between gap-3 py-2.5 hover:opacity-80 transition-opacity">
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-medium"
                      style={{ color:'var(--text)' }}>
                      <AlertCircle size={14} style={{ color:CRIMSON, flexShrink:0 }} />
                      <span className="truncate">{a.title}</span>
                    </span>
                    <span className="block text-xs mt-0.5 truncate" style={{ color:'var(--text-secondary)' }}>
                      {[a.project_name, a.task_title].filter(Boolean).join(' · ') || 'No project'}
                    </span>
                  </span>
                  <span className="text-xs font-semibold whitespace-nowrap" style={{ color:CRIMSON }}>
                    {a.due_date ? new Date(a.due_date).toLocaleDateString(undefined,
                      { day:'numeric', month:'short' }) : ''}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
